// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/authStore';
import { useActiveContextStore } from '@/store/activeContextStore';
import { clearLookupCache } from 'elpo-core-renderer';
import { oturumNabziniBaslat } from '@/auth/oturumNabzi';

const BASE = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');

// DEV'de API hedefi Vite proxy'sinden gecer (vite.config: ELPO_API, varsayilan localhost:5050).
// ONCEDEN localhost'ta SABIT olarak uzak dev sunucusuna baglaniliyordu ("proxy ECONNREFUSED
// veriyor" notuyla). O not bayatlamis — proxy calisiyor (2026-08-04 olculdu: /health 200).
// Sabit uzak adres yuzunden "uygulamayi hep local'den calistir" kurali FIILEN uygulanamiyordu:
// yerelde duzeltilen backend hatasi ekranda gorunmuyor, uzak sunucudaki eski hata gorunuyordu.
// Uzak sunucuya bakmak isteyen: VITE_API_BASE=https://smarterpdev.elpo.com.tr npm run dev
const DEV_API_BASE = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '';

// Istekle birlikte tasinan jeton: 401 geldiginde "bu yanit HANGI oturuma ait" sorusunu cevaplar.
type IstekJetonlu = InternalAxiosRequestConfig & { istekJetonu?: string | null };

/** Oturum sunucu tarafından kapatıldığında (ör. başka yerden giriş) giriş ekranının göstereceği sebep. */
export const CIKIS_SEBEBI_ANAHTARI = 'elpo-cikis-sebebi';

export const apiClient: AxiosInstance = axios.create({
  baseURL: DEV_API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor: JWT + BASE prefix
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().token;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Istegin HANGI jetonla yola ciktigi 401 yanitinda gerekiyor (asagidaki yakalayiciya bak).
  (config as IstekJetonlu).istekJetonu = token ?? null;

  const ctx = useActiveContextStore.getState();
  if (config.headers) {
    if (ctx.activeCompanyId) {
      config.headers['X-Active-Company-Id'] = ctx.activeCompanyId;
    }
    if (ctx.activeBranchId) {
      config.headers['X-Active-Branch-Id'] = ctx.activeBranchId;
    }
    if (ctx.activePeriodId) {
      config.headers['X-Active-Period-Id'] = ctx.activePeriodId;
    }
  }

  if (config.url && config.url.startsWith('/') && !config.url.startsWith(BASE + '/') && BASE) {
    config.url = BASE + config.url;
  }

  // Dosya yuklemede varsayilan JSON basligi kalirsa FormData serilestirilip DOSYA KAYBOLUYOR
  // (govde {"file":{}} gidiyor, sunucu 415 donuyor — banka ekstresi yuklemesi boyle bulundu).
  // Basligi kaldirinca tarayici multipart/form-data + boundary'yi kendisi yazar.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Response interceptor:
//  1) Backend'de olmayan route → ASP.NET SPA fallback HTML 200 dönüyor; bunu error'a çevir
//     (yoksa frontend kodu HTML üzerinde 'in', .json vb. çağrıları yapınca TypeError fırlar)
//  2) 401 → /api/auth/me dışındakilerde logout
apiClient.interceptors.response.use(
  (response) => {
    const data = response.data;
    if (typeof data === 'string' && (data.startsWith('<!DOCTYPE') || data.startsWith('<html'))) {
      const url = response.config?.url || 'bilinmeyen';
      return Promise.reject(
        new AxiosError(
          `Backend endpoint yok (HTML döndü): ${url}`,
          'EBACKENDMISSING',
          response.config,
          response.request,
          response,
        ),
      );
    }
    return response;
  },
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isLoginAttempt = url.includes('/api/auth/login') || url.includes('/api/auth/companies');

      // 401 YALNIZ KENDI OTURUMUNU KAPATIR (2026-09-02 canli olayi).
      // Giris ekraninda bosta baslayan istekler (bildirim, kota, import-formats) jetonsuz yola
      // cikiyor; kullanici o sirada giris yapinca jeton yerine oturuyor ve pano aciliyor. Saniyeler
      // once gonderilmis JETONSUZ istegin 401'i sonra donunce buradaki logout YENI oturumun
      // jetonunu siliyordu -> ekran aninda /login'e geri dusuyordu. Artik yanit, istegin
      // gonderildigi jetonla karsilastiriliyor: yalnizca su anki oturumun kendi istegi
      // reddedildiyse cikis yapilir.
      const aktifJeton = useAuthStore.getState().token;
      const istekJetonu = (error.config as IstekJetonlu | undefined)?.istekJetonu ?? null;
      const kendiOturumunun = aktifJeton !== null && istekJetonu === aktifJeton;

      if (!isLoginAttempt && kendiOturumunun) {
        // Tek oturum (05.10.2026): ayni kullanici baska yerden girdiyse sunucu SESSION_REPLACED doner; giris
        // ekrani sebebi gosterebilsin diye mesaj saklanir (kullanici neden ciktigini bilmeli).
        const govde = error.response.data as { code?: string; error?: string } | undefined;
        if (govde?.code === 'SESSION_REPLACED') {
          try { sessionStorage.setItem(CIKIS_SEBEBI_ANAHTARI, govde.error ?? 'Hesabınıza başka bir yerden giriş yapıldı.'); } catch { /* gizli mod */ }
        }
        apiOnbelleginiTemizle();
        useAuthStore.getState().logout();
      }
    }
    return Promise.reject(error);
  },
);

// ————————————————————————————————————————————————————————————————————————
// GET TEKİLLEŞTİRME (dedupe) — aynı ekran açılışında aynı isteği birden çok kez atmayı önler.
//
// 2026-08-17 ölçümü (Tanklar ekranı, dev): tek ekran açılışında 17 API isteği, toplam 982 ms
// bekleme; bunların 8'i MÜKERRERDİ — CxTank 4 kez, CxStation/CxProduct/export-formats ikişer kez.
// Sebep: aynı veriyi birbirinden habersiz birkaç bileşen (liste, lookup, filtre, dışa aktarma)
// ayrı ayrı istiyor. Tarayıcı 6 eşzamanlı bağlantıyla sınırlı olduğu için fazlalık istekler
// kuyruğa giriyor ve ekran geç doluyor.
//
// İki mekanizma:
//   1) UÇUŞTAKİ istek paylaşımı — aynı adrese giden ikinci istek, birincinin sonucunu bekler.
//   2) ÇOK KISA tazelik penceresi — aynı adres pencerede tekrar istenirse yanıt yeniden kullanılır.
//
// Bayat veri riski yönetildi: pencere kısa (900 ms) ve HER yazma işleminden (POST/PUT/PATCH/DELETE)
// sonra önbellek tamamen boşaltılır — kayıt ekleyip listeye dönen kullanıcı eski listeyi görmez.
// GERİ ALINDI (2026-08-17 ölçümü): 900 ms'lik "taze pencere" varlık detayında lookup etiketlerini
// bozdu — listede dolu olan İstasyon/Ürün alanları detayda "—" göründü. Aynı adrese giden ama FARKLI
// bağlamda kullanılan yanıtlar paylaşılınca etiket eşleşmesi kayboluyor. Doğruluk hızdan önce gelir:
// zaman penceresi KAPALI. Kazancın büyük kısmı zaten uçuştaki paylaşımdan geliyordu (aynı anda giden
// birebir aynı istek — o güvenli, çünkü tek bir yanıtı bekleyen iki tüketici aynı sonucu ister).
const TAZELIK_PENCERESI_MS = 0;

// Bu uçlar her ekran geçişinde yeniden isteniyor ama içerik pratikte sabit: dışa aktarma biçimleri
// derlemeyle gelir, metadata deploy'a kadar değişmez, kullanıcı ayarları ancak kullanıcı
// değiştirdiğinde (o da bir YAZMA işlemidir → önbellek zaten boşalır). Ekranlar arası dolaşırken
// aynı yanıtı defalarca beklememek için pencereleri uzun.
// Yalnız GERÇEKTEN değişmeyen, bağlamdan bağımsız uçlar. Varlık/lookup verisi ASLA buraya girmez.
const UZUN_PENCERE_MS = 5 * 60 * 1000;
// Kullanıcının kendi ayarları (gizli kolon, gizli kayıt, kayıtlı görünüm, ilk kurulum durumu) da
// buradadır: her liste açılışında yeniden soruluyordu (05.10.2026: geçiş başına 4-5 küçük istek).
// Yalnız kullanıcı değiştirince değişir; o bir yazmadır ve önbelleği boşaltır. Giriş de bir yazmadır
// (POST), başka kullanıcının ayarı paylaşılmaz.
const UZUN_PENCERE_DESENLERI = [
  '/api/export/formats',
  '/api/metadata/objects',
  '/api/onboarding/state',
  '/api/record-visibility/hidden',
  '/api/user-settings/',
  '/api/saved-views',
];

function pencereSuresi(url: string): number {
  return UZUN_PENCERE_DESENLERI.some((d) => url.includes(d)) ? UZUN_PENCERE_MS : TAZELIK_PENCERESI_MS;
}

const ucustakiler = new Map<string, Promise<unknown>>();
const tazeYanitlar = new Map<string, { zaman: number; yanit: unknown }>();

function istekAnahtari(config: InternalAxiosRequestConfig): string {
  const ctx = useActiveContextStore.getState();
  // Aktif şirket/şube/dönem yanıtı değiştirir — anahtara girmezse yanlış bağlamın verisi paylaşılırdı.
  return [
    config.url ?? '',
    JSON.stringify(config.params ?? {}),
    ctx.activeCompanyId ?? '',
    ctx.activeBranchId ?? '',
    ctx.activePeriodId ?? '',
  ].join('|');
}

/**
 * Önbelleği VE uçuştaki istek paylaşımını boşaltır.
 *
 * Uçuştakileri de temizlemek şart: giriş yapılmadan önce başlamış bir istek 401 ile biter ve
 * yanıt kesici oturumu kapatır. Paylaşım haritasında kalırsa, girişten HEMEN SONRA aynı adresi
 * isteyen kod o eski (401'e gidecek) sözü alır ve kullanıcı giriş yaptığı hâlde login ekranında
 * kalır — 2026-08-17'de tam bu yaşandı. Kimlik değiştiğinde geçmiş isteklerin hiçbiri geçerli
 * değildir.
 */
export function apiOnbelleginiTemizle(): void {
  tazeYanitlar.clear();
  ucustakiler.clear();
  // Lookup listeleri de tazelenir: yeni eklenen kayıt açılır listede HEMEN görünmeli.
  clearLookupCache();
}

const gercekGet = apiClient.get.bind(apiClient);
apiClient.get = function tekillestirilmisGet(this: unknown, url: string, config?: unknown) {
  const birlesik = { ...(config ?? {}), url } as InternalAxiosRequestConfig;
  const anahtar = istekAnahtari(birlesik);

  const taze = tazeYanitlar.get(anahtar);
  if (taze && Date.now() - taze.zaman < pencereSuresi(url)) {
    return Promise.resolve(taze.yanit) as never;
  }

  const ucustaki = ucustakiler.get(anahtar);
  if (ucustaki) return ucustaki as never;

  const istek = gercekGet(url, config as never)
    .then((yanit) => {
      tazeYanitlar.set(anahtar, { zaman: Date.now(), yanit });
      return yanit;
    })
    .finally(() => {
      ucustakiler.delete(anahtar);
    });

  ucustakiler.set(anahtar, istek);
  return istek as never;
} as typeof apiClient.get;

// Son başarılı yazmanın zamanı. Açık sekmeler arkada donuk durur (KeepAliveOutlet); öne gelen
// liste, kendi yüklemesinden sonra bir yazma olduysa yeniden okur — yoksa istek atmaz.
let sonYazma = 0;
export function sonYazmaZamani(): number {
  return sonYazma;
}

// Yazma isteği geçtiyse taze pencere geçersizdir: kullanıcının az önce yazdığı kayıt görünmeli.
apiClient.interceptors.response.use((response) => {
  const yontem = (response.config?.method ?? 'get').toLowerCase();
  if (yontem !== 'get') {
    sonYazma = Date.now();
    apiOnbelleginiTemizle();
  }
  return response;
});

export interface ApiError {
  readonly error?: string;
  readonly code?: string;
}

/**
 * Bu hata "modül/entity bu kiracıda kurulu değil" mi?
 *
 * Kurulu olmayan bir modülün kartı boş kalmalı — kullanıcıya hata göstermek yanlış olur.
 * Ama 500/timeout gibi GERÇEK hatalar da aynı catch'e düşüp sessizce yutuluyordu: kart
 * "kayıt yok" gösteriyor, halbuki veri gelemiyor. İkisini ayırmak için tek kural burada.
 */
export function eksikModulHatasiMi(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return false;
  const durum = error.response?.status;
  return durum === 400 || durum === 404 || durum === 501;
}

export function extractErrorMessage(error: unknown, fallback = 'Bilinmeyen hata'): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as ApiError | undefined;
    if (data?.error) {
      return data.error;
    }

    // Axios'un kendi metni EKRANA BASILMAZ: "Request failed with status code 500" Türkçe
    // arayüzde İngilizce bir cümledir ve kullanıcıya hiçbir şey anlatmaz. Eskiden buradan
    // dönüyordu, bu yüzden çağıranların verdiği Türkçe fallback pratikte HİÇ kullanılmıyordu
    // (axios.message asla boş olmaz). Teknik metin konsola yazılır, kullanıcı anlamlı cümle görür.
    if (import.meta.env.DEV) console.warn('[api]', error.message, error.config?.url);

    if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message)) {
      return 'İşlem zaman aşımına uğradı, lütfen tekrar deneyin.';
    }
    if (!error.response) {
      return 'Sunucuya ulaşılamıyor, bağlantınızı kontrol edin.';
    }
    const durum = error.response.status;
    if (durum === 401) return 'Oturumunuz sona ermiş, tekrar giriş yapın.';
    if (durum === 403) return 'Bu işlem için yetkiniz yok.';
    if (durum === 404) return 'Kayıt bulunamadı.';
    if (durum === 409) return 'Kayıt başka bir işlemle çakışıyor, sayfayı yenileyip tekrar deneyin.';
    if (durum >= 500) return `${fallback} (sunucu hatası ${durum})`;
    return fallback;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

// Tek oturum: başka yerden giriş yapıldıysa açık ama boşta duran ekran da hemen öğrensin (auth/oturumNabzi.ts).
oturumNabziniBaslat(apiClient, () => useAuthStore.getState().token !== null);
